<template>
  <div class="calender-page">
    <!-- 顶部操作栏 -->
    <v-card variant="flat" border class="pa-2 mb-2 rounded-xl bg-white">
      <div class="d-flex align-center justify-space-between mb-2">
        <!-- 月份切换与标题 -->
        <div class="d-flex align-center">
          <v-btn icon="mdi-chevron-left" variant="text" size="small" @click="prev"></v-btn>
          <span class="text-subtitle-2 font-weight-bold text-grey-darken-3 px-1">
            {{ currentMonthTitle }}
          </span>
          <v-btn icon="mdi-chevron-right" variant="text" size="small" @click="next"></v-btn>
        </div>

        <!-- 学生切换下拉框 -->
        <div style="min-width: 140px; max-width: 180px">
          <v-select
            v-model="selectedStudent"
            :items="student_list"
            item-title="nick_name"
            item-value="ID"
            return-object
            density="compact"
            variant="outlined"
            hide-details
            placeholder="选择学生"
            @update:model-value="onStudentChange"
          ></v-select>
        </div>
      </div>

      <!-- 快捷统计与明细入口 -->
      <div class="d-flex align-center justify-space-between px-1">
        <div class="d-flex align-center">
          <v-chip size="x-small" color="success" variant="tonal" class="font-weight-bold mr-1" @click="dialog = true">
            个训 {{ count.single }}
          </v-chip>
          <v-chip size="x-small" color="teal" variant="tonal" class="font-weight-bold mr-1" @click="dialog = true">
            集体 {{ count.group }}
          </v-chip>
          <v-chip size="x-small" color="deep-purple" variant="tonal" class="font-weight-bold mr-1" @click="dialog = true">
            督导 {{ count.supervision || 0 }}
          </v-chip>
          <v-chip size="x-small" color="primary" variant="tonal" class="font-weight-bold" @click="dialog = true">
            总计 {{ count.total }}
          </v-chip>
        </div>
        <v-btn size="x-small" variant="text" color="primary" class="font-weight-medium" @click="dialog = true">
          <v-icon start icon="mdi-chart-box-outline" size="small" /> 课时明细
        </v-btn>
      </div>
    </v-card>

    <!-- 日历视图主体 -->
    <v-sheet min-height="520" class="rounded-xl overflow-hidden border mb-3">
      <v-calendar
        ref="calendar"
        v-model="focus"
        type="month"
        color="primary"
        :show-month-on-first="false"
        :events="events"
        :event-color="getEventColor"
        :event-height="18"
        :event-more="false"
        @click:event="showType"
        @change="updateRange"
      ></v-calendar>
    </v-sheet>

    <!-- 事件详情与撤销弹窗 (移动端采用原生弹窗，防遮挡) -->
    <v-dialog v-model="selectedOpen" max-width="320">
      <v-card class="rounded-xl overflow-hidden" v-if="selectedEvent">
        <v-toolbar :color="selectedEvent.color || 'primary'" dark density="compact">
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            {{ selectedEvent.type }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-4">
          <!-- 节假日事件 -->
          <template v-if="!selectedEvent.id">
            <div class="text-body-2 font-weight-medium">{{ selectedEvent.name }}</div>
          </template>
          <!-- 打卡记录事件 -->
          <template v-else>
            <div class="d-flex align-center mb-2">
              <v-icon icon="mdi-account" size="small" class="mr-2 text-grey" />
              <span class="text-caption text-grey-darken-1 mr-1">学员：</span>
              <span class="font-weight-bold text-body-2">{{ selectedEvent.student_name || (student ? student.nick_name : "") }}</span>
            </div>

            <div class="d-flex align-center mb-2">
              <v-icon icon="mdi-account-tie" size="small" class="mr-2 text-grey" />
              <span class="text-caption text-grey-darken-1 mr-1">打卡教师：</span>
              <span class="font-weight-bold text-body-2">{{ selectedEvent.teacher_name || selectedEvent.name }}</span>
              <v-chip
                v-if="selectedEvent.user_id === user_id"
                size="x-small"
                color="teal"
                variant="tonal"
                class="ml-2 font-weight-bold"
              >
                我的打卡
              </v-chip>
            </div>

            <div class="d-flex align-center text-caption text-grey-darken-1">
              <v-icon icon="mdi-clock-outline" size="small" class="mr-2 text-grey" />
              <span class="mr-1">打卡时间：</span>
              <span class="font-weight-medium">{{ formatFullTime(selectedEvent.createAt) }}</span>
            </div>

            <div
              v-if="selectedEvent.id && selectedEvent.user_id !== user_id"
              class="text-caption text-grey-darken-1 bg-grey-lighten-4 rounded-lg px-2 py-1 mt-3 text-center"
            >
              非本人打卡不可撤销
            </div>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <div v-if="selectedEvent.id && selectedEvent.user_id === user_id" class="d-flex justify-space-between align-center w-100">
            <v-btn variant="text" size="small" @click="selectedOpen = false">取消</v-btn>
            <v-btn
              color="error"
              variant="tonal"
              size="small"
              class="rounded-pill px-4 font-weight-bold"
              :loading="revoking"
              @click="revoke"
            >
              <v-icon start icon="mdi-undo-variant" size="small" /> 撤销打卡
            </v-btn>
          </div>
          <v-btn
            v-else
            block
            variant="tonal"
            color="grey-darken-1"
            size="small"
            class="rounded-pill"
            @click="selectedOpen = false"
          >
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 课时统计弹窗 -->
    <v-dialog v-model="dialog" max-width="520">
      <v-card class="rounded-xl overflow-hidden">
        <v-toolbar color="primary" dark density="comfortable">
          <v-toolbar-title class="text-subtitle-1 font-weight-bold">课时统计</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" size="small" @click="dialog = false"></v-btn>
        </v-toolbar>

        <v-card-text class="pa-3 pa-sm-4">
          <!-- 统计概览条 -->
          <div class="d-flex align-center justify-space-between pa-3 rounded-lg bg-grey-lighten-4 mb-3">
            <span class="text-caption text-grey-darken-1 font-weight-medium">本月课时累计</span>
            <v-chip color="primary" size="small" class="font-weight-bold">
              总计 {{ count.total }} 节
            </v-chip>
          </div>

          <!-- 7 项课时分类统计网格 (3+4 布局) -->
          <v-row dense class="mb-3">
            <v-col :cols="c.cols" v-for="c in calendarStatCards" :key="c.label">
              <div
                class="pa-2 rounded-lg text-center"
                :style="{
                  borderLeft: `3px solid ${c.value > 0 ? c.color : '#e2e8f0'}`,
                  backgroundColor: '#f8fafc',
                }"
              >
                <div class="text-caption text-grey-darken-1 mb-1">{{ c.label }}</div>
                <div
                  class="text-subtitle-2 font-weight-bold"
                  :style="{ color: c.value > 0 ? c.color : '#94a3b8' }"
                >
                  {{ c.value }}
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- 教师课时明细表格 -->
          <div class="text-caption text-grey-darken-1 mb-2 font-weight-medium">教师课时明细</div>
          <v-table density="compact" hover class="border rounded-lg stat-detail-table">
            <thead>
              <tr>
                <th class="text-left font-weight-bold">教师</th>
                <th class="text-center font-weight-bold text-primary">合计</th>
                <th class="text-center font-weight-bold">个训</th>
                <th class="text-center font-weight-bold">集体</th>
                <th class="text-center font-weight-bold">督导</th>
                <th class="text-center font-weight-bold">20M</th>
                <th class="text-center font-weight-bold">30M</th>
                <th class="text-center font-weight-bold">辅课</th>
                <th class="text-center font-weight-bold">影子</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in teacher_detail" :key="item.name">
                <td class="font-weight-bold text-left" style="white-space: nowrap">
                  {{ item.name }}
                </td>
                <td class="text-center font-weight-bold">
                  <span :class="calcTeacherTotal(item) > 0 ? 'num-total' : 'num-zero'">
                    {{ calcTeacherTotal(item) }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.single || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.single || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.group || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.group || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.supervision || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.supervision || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.connect || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.connect || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.halfHourLesson || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.halfHourLesson || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.subsidiary || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.subsidiary || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="(item.shadow || 0) > 0 ? 'num-active' : 'num-zero'">
                    {{ item.shadow || 0 }}
                  </span>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3 justify-end">
          <v-btn variant="text" @click="dialog = false">关闭</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 底部反馈提示条 -->
    <v-snackbar
      v-model="snackbar.status"
      location="bottom"
      :color="snackbar.color || 'success'"
      :timeout="snackbar.timeout"
      rounded="pill"
      elevation="4"
      class="floating-toast"
    >
      <div class="d-flex align-center">
        <v-icon
          :icon="snackbar.color === 'error' ? 'mdi-alert-circle' : (snackbar.color === 'warning' ? 'mdi-alert' : 'mdi-check-circle')"
          size="18"
          class="mr-2 text-white"
        />
        <span class="text-body-2 font-weight-medium text-white">{{ snackbar.msg }}</span>
      </div>
    </v-snackbar>

    <!-- 悬浮补卡按钮 (FAB) -->
    <v-btn
      icon="mdi-pencil-plus"
      color="primary"
      elevation="4"
      size="large"
      class="fab-buka-btn"
      @click="openBukaDialog"
    ></v-btn>

    <!-- 补卡录入弹窗 -->
    <v-dialog v-model="bukaDialog" persistent max-width="480">
      <v-card class="rounded-xl">
        <v-card-title class="pa-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center text-h6 font-weight-bold">
            <v-icon icon="mdi-backup-restore" class="mr-2" color="primary" />
            补卡录入
          </div>
          <v-btn icon="mdi-close" variant="text" size="small" @click="resetBuka"></v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <!-- 学生多选 -->
          <v-select
            v-model="bukaForm.id"
            :items="student_list"
            item-title="nick_name"
            item-value="ID"
            label="选择学生 *"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="comfortable"
            class="mb-3"
            placeholder="可同时选择多名学生"
          ></v-select>

          <!-- 课程类型选择 (支持多选) -->
          <v-select
            v-model="bukaForm.status"
            :items="courseItems"
            item-title="text"
            item-value="value"
            label="课程类型 (支持多选) *"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="comfortable"
            class="mb-3"
            placeholder="可同时选择多种课程类型"
          ></v-select>

          <!-- 补卡日期选择 (内嵌日历，点击即选) -->
          <div class="mb-3">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-caption text-grey-darken-1 font-weight-medium">补卡日期 (点击直接选择) *</span>
              <v-btn size="x-small" variant="text" color="primary" @click="addBukaToday">添加今天</v-btn>
            </div>

            <!-- 内嵌月份日历选择面板 -->
            <v-card variant="outlined" class="pa-2 rounded-lg buka-calendar-box mb-2">
              <!-- 月份切换 -->
              <div class="d-flex align-center justify-space-between px-1 mb-1">
                <v-btn icon="mdi-chevron-left" variant="text" size="x-small" @click="prevBukaMonth"></v-btn>
                <span class="text-caption font-weight-bold text-grey-darken-3">
                  {{ bukaPickerMonthTitle }}
                </span>
                <v-btn icon="mdi-chevron-right" variant="text" size="x-small" @click="nextBukaMonth"></v-btn>
              </div>

              <!-- 星期表头 -->
              <div class="buka-week-header d-flex text-center mb-1 text-grey text-caption font-weight-medium">
                <span v-for="w in weekDays" :key="w" class="flex-grow-1">{{ w }}</span>
              </div>

              <!-- 7列日期网格 -->
              <div class="buka-days-grid">
                <div
                  v-for="(day, dIdx) in bukaCalendarDays"
                  :key="dIdx"
                  class="buka-day-cell text-center"
                  @click="day.dateStr ? toggleBukaDate(day.dateStr) : null"
                >
                  <div
                    v-if="day.dateStr"
                    class="buka-day-btn"
                    :class="{
                      'buka-day-selected': day.isSelected,
                      'buka-day-today': day.isToday && !day.isSelected,
                    }"
                  >
                    {{ day.dayNum }}
                  </div>
                </div>
              </div>
            </v-card>

            <!-- 已选日期标签区域 -->
            <div v-if="bukaForm.created_at.length > 0" class="d-flex flex-wrap gap-1 mt-1">
              <v-chip
                v-for="(d, idx) in bukaForm.created_at"
                :key="d"
                size="small"
                closable
                color="primary"
                variant="tonal"
                class="mr-1 mb-1 font-weight-medium"
                @click:close="removeBukaDate(idx)"
              >
                {{ d }}
              </v-chip>
            </div>
            <div v-else class="text-caption text-grey text-center py-1">
              请在上方日历中点击选择补卡日期
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3 justify-end">
          <v-btn variant="text" @click="resetBuka">取消</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="rounded-pill px-5 font-weight-bold"
            :loading="bukaSubmitting"
            @click="submitBuka"
          >
            提交补卡
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Footerbar />
  </div>
</template>

<script>
import moment from "moment";
import Footerbar from "@/layout/Footerbar";
import { studentList, getRecord, deleteRecord, createBatchRecord } from "@/api";
import { utc2beijing, statusFilter, statusTransfer } from "@/utils";

export default {
  name: "Calender",
  components: {
    Footerbar,
  },
  data: () => ({
    student_list: [],
    student: {},
    signData: [],
    focus: moment().format("YYYY-MM-DD"),
    dialog: false,
    snackbar: {
      status: false,
      msg: "",
      color: "",
      timeout: 2000,
    },
    selectedStudent: undefined,
    selectedEvent: {},
    selectedOpen: false,
    calendarMonth: "",
    holidays: [
      { name: "元旦", start: "2025-01-01", end: "2025-01-01", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "春节", start: "2025-01-29", end: "2025-02-04", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "春节(班)", start: "2025-01-25", end: "2025-01-25", color: "blue-grey", timed: false, type: "调休" },
      { name: "春节(班)", start: "2025-02-08", end: "2025-02-08", color: "blue-grey", timed: false, type: "调休" },
      { name: "清明节", start: "2025-04-04", end: "2025-04-06", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "劳动节", start: "2025-05-01", end: "2025-05-05", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "劳动节(班)", start: "2025-04-27", end: "2025-04-27", color: "blue-grey", timed: false, type: "调休" },
      { name: "端午节", start: "2025-05-31", end: "2025-06-02", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "中秋节", start: "2025-10-06", end: "2025-10-06", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "国庆节", start: "2025-10-01", end: "2025-10-08", color: "amber-darken-1", timed: false, type: "节假日" },
      { name: "国庆节(班)", start: "2025-09-28", end: "2025-09-28", color: "blue-grey", timed: false, type: "调休" },
      { name: "国庆节(班)", start: "2025-10-11", end: "2025-10-11", color: "blue-grey", timed: false, type: "调休" },
    ],
    count: {
      total: 0,
      single: 0,
      group: 0,
      shadow: 0,
      connect: 0,
      halfHourLesson: 0,
      subsidiary: 0,
      supervision: 0,
    },
    user_id: parseInt(localStorage.getItem("id") || "0"),
    teacher_detail: [],
    events: [],
    bukaDialog: false,
    bukaSubmitting: false,
    revoking: false,
    bukaPickerMonth: moment().format("YYYY-MM"),
    weekDays: ["一", "二", "三", "四", "五", "六", "日"],
    bukaForm: {
      id: [],
      status: [],
      created_at: [],
      user_id: parseInt(localStorage.getItem("id") || "0"),
    },
    courseItems: [
      { text: "个训", value: "1" },
      { text: "集体", value: "2" },
      { text: "督导", value: "7" },
      { text: "影子", value: "3" },
      { text: "辅课", value: "4" },
      { text: "缺席", value: "0" },
      { text: "20MIN", value: "5" },
      { text: "30MIN", value: "6" },
    ],
  }),

  computed: {
    bukaPickerMonthTitle() {
      return moment(this.bukaPickerMonth, "YYYY-MM").format("YYYY年 MM月");
    },
    bukaCalendarDays() {
      const startOfMonth = moment(this.bukaPickerMonth + "-01");
      const daysInMonth = startOfMonth.daysInMonth();
      const firstDayWeek = startOfMonth.isoWeekday(); // 1(周一) ~ 7(周日)
      const todayStr = moment().format("YYYY-MM-DD");

      const days = [];
      // 填充月初空白
      for (let i = 1; i < firstDayWeek; i++) {
        days.push({ dayNum: "", dateStr: null, isToday: false, isSelected: false });
      }
      // 填充当月日期
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = moment(this.bukaPickerMonth + "-01")
          .date(d)
          .format("YYYY-MM-DD");
        days.push({
          dayNum: d,
          dateStr,
          isToday: dateStr === todayStr,
          isSelected: this.bukaForm.created_at.includes(dateStr),
        });
      }
      return days;
    },
    currentMonthTitle() {
      if (this.calendarMonth) {
        return moment(this.calendarMonth).format("YYYY年 MM月");
      }
      return moment().format("YYYY年 MM月");
    },
    calendarStatCards() {
      return [
        { label: "个训", value: this.count.single, color: "#4CAF50", cols: 4 },
        { label: "集体", value: this.count.group, color: "#4DB6AC", cols: 4 },
        { label: "督导", value: this.count.supervision, color: "#673AB7", cols: 4 },
        { label: "20MIN", value: this.count.connect, color: "#607D8B", cols: 3 },
        { label: "30MIN", value: this.count.halfHourLesson, color: "#795548", cols: 3 },
        { label: "辅课", value: this.count.subsidiary, color: "#FB8C00", cols: 3 },
        { label: "影子", value: this.count.shadow, color: "#3F51B5", cols: 3 },
      ];
    },
  },

  mounted() {
    this.getStudentList();
  },

  methods: {
    calcTeacherTotal(item) {
      return (
        (item.single || 0) +
        (item.group || 0) +
        (item.supervision || 0) +
        (item.connect || 0) +
        (item.halfHourLesson || 0) +
        (item.subsidiary || 0) +
        (item.shadow || 0)
      );
    },

    openBukaDialog() {
      this.bukaDialog = true;
      const curStudentId = this.selectedStudent?.ID || this.student?.ID;
      this.bukaForm.id = curStudentId ? [curStudentId] : [];
      this.bukaForm.status = [];
      this.bukaForm.created_at = [];
      this.bukaPickerMonth = moment().format("YYYY-MM");
    },

    prevBukaMonth() {
      this.bukaPickerMonth = moment(this.bukaPickerMonth, "YYYY-MM")
        .subtract(1, "month")
        .format("YYYY-MM");
    },

    nextBukaMonth() {
      this.bukaPickerMonth = moment(this.bukaPickerMonth, "YYYY-MM")
        .add(1, "month")
        .format("YYYY-MM");
    },

    toggleBukaDate(dateStr) {
      const idx = this.bukaForm.created_at.indexOf(dateStr);
      if (idx > -1) {
        this.bukaForm.created_at.splice(idx, 1);
      } else {
        this.bukaForm.created_at.push(dateStr);
        this.bukaForm.created_at.sort();
      }
    },

    addBukaToday() {
      const today = moment().format("YYYY-MM-DD");
      if (!this.bukaForm.created_at.includes(today)) {
        this.bukaForm.created_at.push(today);
        this.bukaForm.created_at.sort();
      }
      this.bukaPickerMonth = moment().format("YYYY-MM");
    },

    removeBukaDate(idx) {
      this.bukaForm.created_at.splice(idx, 1);
    },

    resetBuka() {
      this.bukaDialog = false;
      this.bukaSubmitting = false;
      this.bukaForm = {
        id: [],
        status: [],
        created_at: [],
        user_id: parseInt(localStorage.getItem("id") || "0"),
      };
      this.bukaPickerMonth = moment().format("YYYY-MM");
    },

    submitBuka() {
      if (
        !this.bukaForm.id ||
        this.bukaForm.id.length === 0 ||
        !this.bukaForm.status ||
        this.bukaForm.status.length === 0 ||
        !this.bukaForm.created_at ||
        this.bukaForm.created_at.length === 0
      ) {
        this.snackbar.status = true;
        this.snackbar.msg = "请完整选择学生、课程和至少一个日期";
        this.snackbar.color = "warning";
        return;
      }

      this.bukaSubmitting = true;
      const signTime = [];
      for (let i = 0; i < this.bukaForm.created_at.length; i++) {
        signTime.push(moment(this.bukaForm.created_at[i]).utc().format());
      }

      // 支持课程类型多选：单次批量请求提交
      const statusList = Array.isArray(this.bukaForm.status)
        ? this.bukaForm.status
        : [this.bukaForm.status];
      const statuses = statusList.map((st) => parseInt(st));

      createBatchRecord({
        statuses: statuses,
        status: statuses[0] || 0, // 兼容老字段
        student_ids: this.bukaForm.id,
        time_set: signTime,
        user_id: this.bukaForm.user_id,
      })
        .then((res) => {
          this.bukaSubmitting = false;
          if (res.data?.code === "200" || res.data?.code === 200 || res.code === 0 || res.code === 200) {
            this.bukaDialog = false;
            this.snackbar.status = true;
            this.snackbar.msg = "补卡成功";
            this.snackbar.color = "success";
            const targetStudent = this.selectedStudent || this.student;
            if (targetStudent && targetStudent.ID) {
              this.getRecordByStudentId(targetStudent);
            }
          } else {
            this.snackbar.status = true;
            this.snackbar.msg = res.data?.msg || res.msg || "补卡失败";
            this.snackbar.color = "error";
          }
        })
        .catch(() => {
          this.bukaSubmitting = false;
          this.snackbar.status = true;
          this.snackbar.msg = "提交异常，请稍后重试";
          this.snackbar.color = "error";
        });
    },

    formatFullTime(utcTime) {
      if (!utcTime) return "";
      return moment(utcTime * 1000).format("YYYY-MM-DD HH:mm:ss");
    },

    transferTime(utcTime) {
      if (!utcTime) return "";
      return utc2beijing(utcTime);
    },

    onStudentChange(newStudent) {
      this.getRecordByStudentId(newStudent);
    },

    revoke() {
      const eventId = this.selectedEvent.id;
      if (this.selectedEvent.user_id !== this.user_id) {
        this.selectedOpen = false;
        this.snackbar.status = true;
        this.snackbar.msg = "不能撤销其他用户打卡记录";
        this.snackbar.color = "warning";
        return;
      }

      this.revoking = true;
      deleteRecord({ id: eventId })
        .then((res) => {
          this.selectedOpen = false;
          if (res.data.code === "200" || res.data.code === 200) {
            this.snackbar.status = true;
            this.snackbar.msg = "撤销成功";
            this.snackbar.color = "success";
          } else {
            this.snackbar.status = true;
            this.snackbar.msg = res.data.msg || "撤销失败";
            this.snackbar.color = "warning";
          }
          this.getRecordByStudentId(this.student);
        })
        .catch(() => {
          this.snackbar.status = true;
          this.snackbar.msg = "撤销失败，请稍后重试";
          this.snackbar.color = "warning";
        })
        .finally(() => {
          this.revoking = false;
        });
    },

    showType(arg1, arg2) {
      let target = (arg2 && arg2.event) ? arg2.event : (arg1 && arg1.event ? arg1.event : (arg2 || arg1));
      if (target && target.input) {
        target = { ...target.input, ...target };
      }
      if (!target || (typeof Event !== "undefined" && target instanceof Event) || target.type === "click") {
        return;
      }
      this.selectedEvent = target;
      this.selectedOpen = true;
      const nativeEv = arg1 instanceof Event ? arg1 : (arg2?.nativeEvent || arg1?.nativeEvent);
      if (nativeEv && nativeEv.stopPropagation) {
        nativeEv.stopPropagation();
      }
    },

    getRecordByStudentId(studentObj, move) {
      if (!studentObj || !studentObj.ID) return;
      let month;
      if (this.calendarMonth !== "") {
        month = this.months(move);
      } else {
        month = this.months(move);
      }

      const startTime = month.startOf("month").format("YYYY-MM-DD HH:mm:ss.SSS");
      const endTime = month.endOf("month").format("YYYY-MM-DD HH:mm:ss.SSS");

      getRecord({
        student_id: parseInt(studentObj.ID),
        start_at: startTime,
        end_at: endTime,
      }).then((res) => {
        const data = res.data?.data || {};
        const detail = data.detail || {};
        this.signData = data.records || [];
        this.count.group = detail.group || 0;
        this.count.single = detail.single || 0;
        this.count.total = detail.total || 0;
        this.count.shadow = detail.shadow || 0;
        this.count.connect = detail.connect || 0;
        this.count.halfHourLesson = detail.halfHourLesson || 0;
        this.count.supervision = detail.supervision || 0;
        this.count.subsidiary = detail.subsidiary || 0;
        this.teacher_detail = detail.user_detail || [];

        this.student = studentObj;
        this.updateRange();
      });
    },

    months(move) {
      let base = this.calendarMonth ? moment(this.calendarMonth) : moment();
      if (move === "prev") {
        base = base.subtract(1, "months");
        this.calendarMonth = base;
      } else if (move === "next") {
        base = base.add(1, "months");
        this.calendarMonth = base;
      } else if (!this.calendarMonth) {
        this.calendarMonth = moment();
      }
      return moment(this.calendarMonth);
    },

    getStudentList() {
      studentList({}).then((res) => {
        this.student_list = res.data?.data || [];
        if (this.student_list.length > 0) {
          this.student = this.student_list[this.student_list.length - 1];
          this.selectedStudent = this.student;

          const month = moment();
          this.calendarMonth = month;
          const startTime = month.startOf("month").format("YYYY-MM-DD HH:mm:ss.SSS");
          const endTime = month.endOf("month").format("YYYY-MM-DD HH:mm:ss.SSS");

          getRecord({
            student_id: parseInt(this.student.ID),
            start_at: startTime,
            end_at: endTime,
          }).then((recordRes) => {
            const data = recordRes.data?.data || {};
            const detail = data.detail || {};
            this.signData = data.records || [];
            this.count.group = detail.group || 0;
            this.count.single = detail.single || 0;
            this.count.total = detail.total || 0;
            this.count.shadow = detail.shadow || 0;
            this.count.connect = detail.connect || 0;
            this.count.halfHourLesson = detail.halfHourLesson || 0;
            this.count.supervision = detail.supervision || 0;
            this.count.subsidiary = detail.subsidiary || 0;
            this.teacher_detail = detail.user_detail || [];
            this.updateRange();
          });
        }
      });
    },

    getEventColor(event) {
      return event.color || "primary";
    },

    prev() {
      if (this.$refs.calendar?.prev) {
        this.$refs.calendar.prev();
      }
      this.getRecordByStudentId(this.student, "prev");
    },

    next() {
      if (this.$refs.calendar?.next) {
        this.$refs.calendar.next();
      }
      this.getRecordByStudentId(this.student, "next");
    },

    updateRange() {
      const events = [];

      for (let i = 0; i < this.signData.length; i++) {
        const item = this.signData[i];
        const create_time = new Date(item.created_at * 1000);

        events.push({
          name: item.teacher_name,
          teacher_name: item.teacher_name,
          student_name: item.nick_name || this.student?.nick_name || this.selectedStudent?.nick_name || "",
          start: create_time,
          details: item.status > 0 ? "出勤" : "缺勤",
          createAt: item.created_at,
          color: statusFilter(item.status),
          timed: false,
          type: statusTransfer(item.status),
          id: item.id,
          user_id: item.user_id,
        });
      }

      this.events = [...events, ...this.holidays];
    },
  },
};
</script>

<style scoped>
.calender-page {
  max-width: 600px;
  margin: 0 auto;
  padding: 12px;
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
}

.fab-buka-btn {
  position: fixed;
  right: 20px;
  bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  z-index: 99;
}

:deep(.v-calendar-weekly) {
  height: auto !important;
}

:deep(.v-calendar-weekly__week) {
  flex: 1 0 auto !important;
  min-height: 85px;
}

:deep(.v-calendar-weekly__day) {
  min-height: 85px;
  height: auto !important;
  overflow: visible !important;
  padding-bottom: 4px !important;
}

.buka-calendar-box {
  background-color: #fafafa;
  border-color: #e0e0e0;
}
.buka-week-header {
  padding: 2px 0;
}
.buka-days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
}
.buka-day-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
}
.buka-day-btn {
  width: 28px;
  height: 28px;
  line-height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease-in-out;
  color: #333333;
}
.buka-day-btn:active {
  transform: scale(0.9);
}
.buka-day-selected {
  background-color: #1976D2 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  box-shadow: 0 2px 5px rgba(25, 118, 210, 0.4);
}
.buka-day-today {
  border: 1.5px solid #1976D2;
  color: #1976D2;
  font-weight: 700;
}

/* 弹窗表格横向滚动与热力药丸样式 */
.stat-detail-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.stat-detail-table {
  font-size: 12px;
  width: 100%;
}

.stat-detail-table th {
  background-color: #f8fafc;
  color: #64748b;
  padding: 7px 2px !important;
  font-size: 11.5px;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.stat-detail-table td {
  padding: 6px 2px !important;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

/* 首列“教师”吸附固定 (Sticky Left) */
.stat-detail-table th:first-child,
.stat-detail-table td:first-child {
  position: sticky;
  left: 0;
  border-right: 1px solid #e2e8f0;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
}

.stat-detail-table th:first-child {
  z-index: 3;
  background-color: #f8fafc;
  padding-left: 6px !important;
  padding-right: 4px !important;
}

.stat-detail-table td:first-child {
  z-index: 2;
  background-color: #ffffff;
  padding-left: 6px !important;
  padding-right: 4px !important;
}

.stat-detail-table tbody tr:hover td:first-child {
  background-color: #f8fafc;
}

.num-zero {
  color: #94a3b8;
  font-weight: 400;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.num-active {
  color: #0f172a;
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.num-total {
  color: #1867c0;
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
</style>
